import { Component, computed, input, output } from '@angular/core';
import { LpWizardTargetDirective } from '@gametailors/leerplus';
import { cn } from '@gametailors/web-core/tailwind';
import { cva, type VariantProps } from 'class-variance-authority';
import { IconKlankvoetNormal } from '../../../icons/i-klankvoet-normal';
import { IconKlankvoetStom } from '../../../icons/i-klankvoet-stom';
import { RidSyllabicKeyboardUtils, TKey } from '../rid-syllabic-keyboard.utils';

const keyboardKeyVariants = cva(
  'font-default rounded-sm border-2 px-3 py-2 text-2xl font-normal text-text-primary bg-white hover:bg-surface-100 border-surface-300 cursor-pointer',
  {
    variants: {
      type: {
        GROUP: 'font-bold border-purple-300 bg-purple-50 hover:bg-purple-200',
        KLANK: '',
        EXCEPTION: 'font-bold border-0 bg-orange-100 text-orange-600 hover:bg-orange-200',
        SPACE: '',
        KLANKVOET_NORMAL: 'text-klankgroep-normaal',
        KLANKVOET_STOM: 'text-klankgroep-stom',
        RULE: 'font-bold text-klankgroep-normaal border-klankgroep-normaal border-dashed bg-white hover:bg-klankgroep-normaal/10',
      },
      klankStyle: {
        default: '',
        longShort:
          'rounded-xs rounded-tl-none rounded-bl-none border-0 border-l-2 border-dotted border-klankgroep-normaal text-klankgroep-normaal font-bold',
        long: 'border-none rounded-xs',
        sjwa: 'italic text-klankgroep-stom',
      },
      disabled: {
        true: 'border-transparent text-slate-400 bg-transparent hover:bg-transparent',
        false: '',
      },
      highlight: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        disabled: true,
        highlight: true,
        class: 'border-klankgroep-normaal/15 bg-klankgroep-normaal/8 text-klankgroep-normaal/45 hover:bg-klankgroep-normaal/8',
      },
      {
        disabled: false,
        highlight: true,
        class: 'border-klankgroep-normaal/25 bg-klankgroep-normaal/10 text-klankgroep-normaal hover:bg-klankgroep-normaal/12',
      },
      { type: 'EXCEPTION', disabled: true, class: 'border-0 bg-orange-50 text-orange-300 hover:bg-orange-50' },
      { type: 'EXCEPTION', disabled: false, highlight: true, class: 'border-0 bg-orange-200 text-orange-700 hover:bg-orange-200' },
      { type: 'EXCEPTION', disabled: true, highlight: true, class: 'border-0 bg-orange-50 text-orange-300 hover:bg-orange-50' },
      { disabled: false, class: 'cursor-pointer' },
      { disabled: true, class: 'cursor-default' },
    ],
    defaultVariants: {
      klankStyle: 'default',
      disabled: false,
      highlight: false,
    },
  },
);

type KeyboardKeyVariants = VariantProps<typeof keyboardKeyVariants>;

@Component({
  selector: 'rid-keyboard-key',
  imports: [IconKlankvoetNormal, IconKlankvoetStom, LpWizardTargetDirective],
  templateUrl: './rid-keyboard-key.html',
  host: {
    '[class.hidden]': 'hidden()',
  },
})
export class RidKeyboardKey {
  protected getDisplayText = RidSyllabicKeyboardUtils.getDisplayText.bind(RidSyllabicKeyboardUtils);

  key = input.required<TKey>();
  hidden = input<boolean>(false);
  hasHighlight = input<boolean>(false);
  disabled = input<boolean>(false);
  animateEnter = input<boolean>(true);

  pressed = output<void>();

  twClasses = computed<string>(() => {
    return cn(
      keyboardKeyVariants({
        type: this.resolveType(this.key()),
        klankStyle: this.resolveKlankStyle(this.key()),
        disabled: this.disabled(),
        highlight: this.hasHighlight(),
      }),
    );
  });

  private resolveType(key: TKey): KeyboardKeyVariants['type'] {
    switch (key.type) {
      case 'KLANKVOET':
        return key.value === '>' ? 'KLANKVOET_NORMAL' : 'KLANKVOET_STOM';
      default:
        return key.type;
    }
  }

  private resolveKlankStyle(key: TKey): KeyboardKeyVariants['klankStyle'] {
    if (key.type !== 'KLANK' && key.type !== 'SPACE') return 'default';

    if (key.value.endsWith(')')) return 'longShort';
    if (key.value.length === 2 && key.value[0] === key.value[1]) return 'long';
    if (['sjwa', 'ig', 'lijk'].includes(key.value)) return 'sjwa';

    return 'default';
  }

  press() {
    if (this.disabled()) return;

    this.pressed.emit();
  }
}
